import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
} from '@mui/material';
import { EventAvailable as PtoIcon, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { enIN, enUS } from 'date-fns/locale';
import { format, isSameDay, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { toast } from 'react-toastify';
import api from '../../utils/api';

function parseIsoDateOnly(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function toIsoDate(d) {
  if (!d) return '';
  return format(d, 'yyyy-MM-dd');
}

function eachYmdBetweenIso(startIso, endIso) {
  const out = [];
  let cur = parseIsoDateOnly(startIso);
  const end = parseIsoDateOnly(endIso);
  if (!cur || !end) return out;
  const a = cur <= end ? cur : end;
  const b = cur <= end ? end : cur;
  let x = a;
  while (x <= b) {
    out.push(toIsoDate(x));
    x = new Date(x.getFullYear(), x.getMonth(), x.getDate() + 1, 12, 0, 0, 0);
  }
  return out;
}

const defaultCtx = () => ({
  server_year: new Date().getFullYear(),
  server_time_iso: '',
  server_time_in_org_tz: '',
  org: {
    default_country_code: 'US',
    default_timezone: 'America/Los_Angeles',
    default_currency: 'USD',
    date_locale: 'en-US',
    date_format_label: 'MM/DD/YYYY (United States)',
  },
  alternate_calendar: null,
  approvers: { managers: [], administrators: [] },
});

export default function MyPtoApply() {
  const [ctx, setCtx] = useState(defaultCtx);
  const [holidaySet, setHolidaySet] = useState(new Set());
  const [holidaysMeta, setHolidaysMeta] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [extraIsoDates, setExtraIsoDates] = useState([]);
  const [singlePicker, setSinglePicker] = useState(() => new Date());
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [submitResult, setSubmitResult] = useState(null);

  const serverYear = ctx.server_year || new Date().getFullYear();
  const org = ctx.org || defaultCtx().org;
  const adapterLocale = org.date_locale === 'en-IN' ? enIN : enUS;

  const formatPrimary = useCallback(
    (d) => {
      if (!d) return '—';
      if (org.date_locale === 'en-IN') return format(d, 'dd/MM/yyyy', { locale: enIN });
      return format(d, 'MM/dd/yyyy', { locale: enUS });
    },
    [org.date_locale]
  );

  const formatAltCalendar = useCallback((isoYmd) => {
    const d = parseIsoDateOnly(isoYmd);
    if (!d) return '—';
    const cc = ctx.alternate_calendar?.country_code;
    if (cc === 'IN') return format(d, 'dd/MM/yyyy', { locale: enIN });
    return format(d, 'MM/dd/yyyy', { locale: enUS });
  }, [ctx.alternate_calendar]);

  const loadHolidays = useCallback(async (year, countryCode) => {
    const cc = String(countryCode || org.default_country_code || 'US')
      .trim()
      .toUpperCase()
      .slice(0, 2);
    try {
      const r = await api.get('/pto/published-holidays', {
        params: { year, country_code: cc },
      });
      const d = r.data?.data || {};
      const arr = Array.isArray(d.disabled_dates) ? d.disabled_dates : [];
      setHolidaySet(new Set(arr));
      setHolidaysMeta(Array.isArray(d.holidays) ? d.holidays : []);
    } catch {
      setHolidaySet(new Set());
      setHolidaysMeta([]);
    }
  }, [org.default_country_code]);

  const loadMy = useCallback(async () => {
    try {
      const r = await api.get('/pto/my-requests');
      setMyRequests(Array.isArray(r.data?.data?.requests) ? r.data.data.requests : []);
    } catch {
      setMyRequests([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const cr = await api.get('/pto/context');
        if (cancelled) return;
        const d = cr.data?.data || {};
        const next = {
          server_year: d.server_year ?? new Date().getFullYear(),
          server_time_iso: d.server_time_iso || '',
          server_time_in_org_tz: d.server_time_in_org_tz || '',
          org: {
            default_country_code: d.org?.default_country_code || 'US',
            default_timezone: d.org?.default_timezone || 'America/Los_Angeles',
            default_currency: d.org?.default_currency || 'USD',
            date_locale: d.org?.date_locale || 'en-US',
            date_format_label: d.org?.date_format_label || 'MM/DD/YYYY',
          },
          alternate_calendar: d.alternate_calendar || null,
          approvers: d.approvers || { managers: [], administrators: [] },
        };
        setCtx(next);
        const sy = next.server_year;
        await loadHolidays(sy, next.org.default_country_code);
        if (cancelled) return;
        setCalendarMonth(new Date(sy, 0, 1));
        await loadMy();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadHolidays, loadMy]);

  useEffect(() => {
    const y = calendarMonth.getFullYear();
    loadHolidays(y, org.default_country_code);
  }, [calendarMonth, loadHolidays, org.default_country_code]);

  const rangeIsoList = useMemo(() => {
    if (!fromDate || !toDate) return [];
    const a = startOfDay(fromDate) <= startOfDay(toDate) ? fromDate : toDate;
    const b = startOfDay(fromDate) <= startOfDay(toDate) ? toDate : fromDate;
    return eachYmdBetweenIso(toIsoDate(a), toIsoDate(b));
  }, [fromDate, toDate]);

  const mergedSortedIso = useMemo(() => {
    const s = new Set();
    for (const x of rangeIsoList) {
      if (!holidaySet.has(x)) s.add(x);
    }
    for (const x of extraIsoDates) {
      if (!holidaySet.has(x)) s.add(x);
    }
    return [...s].sort();
  }, [rangeIsoList, extraIsoDates, holidaySet]);

  const shouldDisableDate = useCallback(
    (d) => {
      const key = format(d, 'yyyy-MM-dd');
      return holidaySet.has(key);
    },
    [holidaySet]
  );

  const handleCalendarChange = (newDate) => {
    if (!newDate || shouldDisableDate(newDate)) return;
    if (!fromDate || newDate < startOfDay(fromDate)) {
      setFromDate(newDate);
      setToDate(null);
      return;
    }
    if (!toDate || !isSameDay(newDate, toDate)) {
      setToDate(newDate);
    }
  };

  const handleAddSingleDate = () => {
    const key = toIsoDate(singlePicker);
    if (!key) return;
    if (holidaySet.has(key)) {
      toast.error('That date is a published public holiday and cannot be taken as leave.');
      return;
    }
    if (extraIsoDates.includes(key)) {
      toast.info('That date is already in your individual list.');
      return;
    }
    setExtraIsoDates((prev) => [...prev, key].sort());
  };

  const handleSubmit = async () => {
    if (!mergedSortedIso.length) {
      toast.error('Choose a from–to range and/or add individual dates (at least one working day).');
      return;
    }
    const rtxt = reason.trim();
    if (rtxt.length < 3) {
      toast.error('Please enter a reason for leave (at least a few characters).');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/pto/my-requests', {
        selected_dates: mergedSortedIso,
        reason: rtxt,
      });
      const payload = r.data?.data || {};
      toast.success(r.data?.message || 'Leave submitted.');
      setSubmitResult({
        submitted_to: payload.submitted_to || { managers: [], administrators: [] },
        approval_notice: payload.approval_notice || '',
      });
      setFromDate(null);
      setToDate(null);
      setExtraIsoDates([]);
      setReason('');
      await loadMy();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Submit failed.');
    } finally {
      setSaving(false);
    }
  };

  const CustomDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const ds = format(day, 'yyyy-MM-dd');
    const blocked = holidaySet.has(ds);
    const inExtras = extraIsoDates.includes(ds);
    let inRange = false;
    if (fromDate && toDate) {
      inRange = isWithinInterval(day, { start: startOfDay(fromDate), end: endOfDay(toDate) });
    } else if (fromDate && isSameDay(day, fromDate)) {
      inRange = true;
    }
    const highlight = (inRange || inExtras) && !blocked;
    return (
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        disabled={blocked}
        sx={{
          ...(blocked
            ? {
                bgcolor: 'action.disabledBackground',
                color: 'text.disabled',
                textDecoration: 'line-through',
              }
            : {}),
          ...(highlight
            ? {
                bgcolor: inExtras ? 'secondary.light' : 'primary.light',
                color: 'primary.contrastText',
                fontWeight: 700,
              }
            : {}),
        }}
      />
    );
  };

  const ccLabel = org.default_country_code === 'IN' ? 'India' : org.default_country_code === 'US' ? 'USA' : org.default_country_code;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PtoIcon color="primary" />
        My PTO — Apply Leave
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Calendar year from server: <strong>{serverYear}</strong>
        {ctx.server_time_in_org_tz ? (
          <>
            {' '}
            · Organization time ({org.default_timezone}): <strong>{ctx.server_time_in_org_tz}</strong>
          </>
        ) : ctx.server_time_iso ? (
          <>
            {' '}
            · Server (UTC): {new Date(ctx.server_time_iso).toISOString()}
          </>
        ) : null}
        . Defaults follow <strong>Administration → Settings → Default values</strong>: country <strong>{ccLabel}</strong>
        {' '}({org.default_country_code}), dates <strong>{org.date_format_label}</strong>, currency{' '}
        <strong>{org.default_currency}</strong>. Published public holidays for that country are greyed out.
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Until your leave is approved</strong>, the system may still count these days as working time for you.
        This request is sent to your <strong>manager</strong> (if assigned) and <strong>administrators</strong> for review —
        please ensure it is approved before relying on it as PTO.
      </Alert>

      {submitResult ? (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSubmitResult(null)}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Submitted for approval
          </Typography>
          {submitResult.approval_notice ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {submitResult.approval_notice}
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Managers:</strong>{' '}
            {submitResult.submitted_to?.managers?.length
              ? submitResult.submitted_to.managers.join(', ')
              : '— (none on file)'}
          </Typography>
          <Typography variant="body2">
            <strong>Administrators:</strong>{' '}
            {submitResult.submitted_to?.administrators?.length
              ? submitResult.submitted_to.administrators.join(', ')
              : '—'}
          </Typography>
        </Alert>
      ) : null}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Select leave (range and/or individual days)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click a day for <strong>from</strong>, then another for <strong>to</strong> (inclusive range). Use{' '}
          <strong>Add single date</strong> for extra scattered days; they merge with the range. Purple highlight = individual
          day; blue = range. Grey = public holiday.
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
            <DateCalendar
              referenceDate={calendarMonth}
              onMonthChange={(d) => setCalendarMonth(d)}
              value={null}
              onChange={handleCalendarChange}
              shouldDisableDate={shouldDisableDate}
              slots={{ day: CustomDay }}
            />
            <Box sx={{ flex: '1 1 320px' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>From:</strong> {formatPrimary(fromDate)}{' '}
                <strong>To:</strong> {formatPrimary(toDate)}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  size="small"
                  label="Add individual date"
                  InputLabelProps={{ shrink: true }}
                  value={toIsoDate(singlePicker)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    const d = parseIsoDateOnly(v);
                    if (d) setSinglePicker(d);
                  }}
                />
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddSingleDate}>
                  Add to list
                </Button>
              </Stack>
              {extraIsoDates.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Individual days (not necessarily inside the range)
                  </Typography>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {extraIsoDates.map((iso) => (
                      <Chip
                        key={iso}
                        size="small"
                        label={formatPrimary(parseIsoDateOnly(iso))}
                        onDelete={() => setExtraIsoDates((p) => p.filter((x) => x !== iso))}
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Selected working days ({mergedSortedIso.length}):</strong>
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontFamily: 'monospace', wordBreak: 'break-word', whiteSpace: 'normal' }}
              >
                {mergedSortedIso.length ? mergedSortedIso.map((iso) => formatPrimary(parseIsoDateOnly(iso))).join(', ') : '—'}
              </Typography>
              {ctx.alternate_calendar && mergedSortedIso.length > 0 ? (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {ctx.alternate_calendar.label} — same civil dates ({ctx.alternate_calendar.date_format_label})
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {mergedSortedIso.map((iso) => formatAltCalendar(iso)).join(', ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {ctx.alternate_calendar.note}
                  </Typography>
                </Box>
              ) : null}
              <Typography variant="h6" sx={{ mb: 2 }}>
                Total days: <Chip label={mergedSortedIso.length || '—'} color="primary" />
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Reason for leave"
                placeholder="Describe why you are applying for this leave…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button variant="contained" onClick={handleSubmit} disabled={saving || mergedSortedIso.length < 1}>
                {saving ? <CircularProgress size={22} /> : 'Submit for approval'}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Sent to:{' '}
                <strong>{ctx.approvers?.managers?.join(', ') || '—'}</strong> (managers) ·{' '}
                <strong>{ctx.approvers?.administrators?.join(', ') || '—'}</strong> (administrators)
              </Typography>
            </Box>
          </Box>
        </LocalizationProvider>
        {holidaysMeta.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {holidaysMeta.length} published holiday(s) loaded for {org.default_country_code} this year — those dates are
            blocked.
          </Alert>
        )}
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        My requests
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Days / selected</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {myRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No requests yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              myRequests.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.start_date ? formatPrimary(parseIsoDateOnly(row.start_date)) : '—'}
                  </TableCell>
                  <TableCell>
                    {row.end_date ? formatPrimary(parseIsoDateOnly(row.end_date)) : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, whiteSpace: 'normal' }}>
                    <Chip size="small" label={row.calendar_days} sx={{ mr: 0.5 }} />
                    {Array.isArray(row.selected_dates) && row.selected_dates.length ? (
                      <Typography variant="caption" component="span" sx={{ display: 'block', fontFamily: 'monospace' }}>
                        {row.selected_dates.map((iso) => formatPrimary(parseIsoDateOnly(iso))).join(', ')}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal' }}>{row.reason}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'error' : 'warning'
                      }
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
